import { coordOf, idx, inBounds, isEdge, isRoad, tileAt, touchesCore, tracePath } from "./board.js";
import {
  MAX_UNITS_PER_ATTACK,
  MAX_WAVES,
  MAX_WAVE_OFFSET_TICKS,
  isCore,
  maxTowersOfOneType,
  towerSlots,
} from "./constants.js";
import { defenceBudget, towerStats } from "./data.js";
import { TOWER_KINDS, UNIT_KINDS } from "./types.js";
import type { AttackPlan, Coord, Layout, TowerKind, ValidationResult } from "./types.js";

/**
 * Rejects illegal defender layouts before a battle is ever simulated.
 *
 * The server calls this when a defender commits a layout; the editor (S07) calls
 * the same function to grey out the Save button and show the offending tiles.
 * One implementation, so the client can never draw something the server refuses.
 *
 * Checks run cheapest-and-most-fundamental first and return the FIRST failure,
 * because a defender who has drawn a branch does not also need to be told about
 * their tower budget — they need to see the branch.
 *
 * Rules come from 02 GDD §6.1–6.3 and the Defence Budget sheet.
 */
export function validateLayout(layout: Layout): ValidationResult {
  return (
    checkShape(layout) ??
    checkCoreTiles(layout) ??
    checkGate(layout) ??
    checkPath(layout) ??
    checkBudgets(layout) ??
    checkTowers(layout) ?? { ok: true }
  );
}

type Failure = { readonly ok: false; readonly error: { code: string; message: string; tiles: Coord[] } };

function fail(code: string, message: string, tiles: Coord[] = []): Failure {
  return { ok: false, error: { code, message, tiles } };
}

// --- structure ------------------------------------------------------------

function checkShape(layout: Layout): Failure | undefined {
  if (layout.tiles.length !== layout.size * layout.size) {
    return fail(
      "SIZE_MISMATCH",
      `Expected ${layout.size * layout.size} tiles, got ${layout.tiles.length}.`
    );
  }
  if (layout.wallLevel < 0 || layout.engineersGuildLevel < 0) {
    return fail("NEGATIVE_LEVEL", "Wall and Engineer's Guild levels cannot be negative.");
  }
  return undefined;
}

/** The core is fixed at the central 2x2. Nothing may be built on it. */
function checkCoreTiles(layout: Layout): Failure | undefined {
  const wrong: Coord[] = [];
  for (let i = 0; i < layout.tiles.length; i++) {
    const at = coordOf(i, layout.size);
    const kind = layout.tiles[i]?.kind;
    const shouldBeCore = isCore(at.x, at.y);
    if (shouldBeCore && kind !== "core") wrong.push(at);
    if (!shouldBeCore && kind === "core") wrong.push(at);
  }
  if (wrong.length > 0) {
    return fail(
      "CORE_MISPLACED",
      "The core is the central 2x2 and cannot be moved or built over.",
      wrong
    );
  }
  return undefined;
}

function checkGate(layout: Layout): Failure | undefined {
  const { x, y } = layout.gate;
  if (!inBounds(x, y, layout.size)) {
    return fail("GATE_OFF_BOARD", "The gate is outside the board.", [layout.gate]);
  }
  if (!isEdge(x, y, layout.size)) {
    return fail("GATE_NOT_ON_EDGE", "The gate must sit on a board edge tile.", [layout.gate]);
  }
  return undefined;
}

// --- the road -------------------------------------------------------------

function checkPath(layout: Layout): Failure | undefined {
  const traced = tracePath(layout);

  if (!traced.ok) {
    return fail(traced.code, PATH_MESSAGES[traced.code] ?? "The road is not a single path.", [
      ...traced.tiles,
    ]);
  }

  const path = traced.path;
  const end = path[path.length - 1]!;

  if (!touchesCore(end.x, end.y)) {
    return fail(
      "ROAD_DOES_NOT_REACH_CORE",
      "The road must end on a tile next to the core.",
      [end]
    );
  }

  // Only the final tile may touch the core: a road that brushes past it would
  // let units reach the core early and make the maze pointless.
  const early = path.slice(0, -1).filter((c) => touchesCore(c.x, c.y));
  if (early.length > 0) {
    return fail(
      "ROAD_PASSES_CORE",
      "Only the last road tile may touch the core.",
      early
    );
  }

  return undefined;
}

const PATH_MESSAGES: Record<string, string> = {
  GATE_NOT_ROAD: "The gate tile must be part of the road.",
  GATE_ISOLATED: "The road must continue from the gate.",
  GATE_BRANCHES: "The gate must have exactly one road tile leading from it.",
  ROAD_BRANCHES: "The road cannot fork. Every tile needs exactly two road neighbours.",
  ROAD_LOOPS: "The road cannot loop back on itself.",
  ROAD_NOT_CONNECTED: "Every road tile must connect to the path from the gate.",
};

// --- budgets --------------------------------------------------------------

function checkBudgets(layout: Layout): Failure | undefined {
  const budget = defenceBudget(Math.min(layout.wallLevel, lastWallLevel()));

  const road: Coord[] = [];
  const mud: Coord[] = [];
  const barricade: Coord[] = [];

  for (let i = 0; i < layout.tiles.length; i++) {
    const kind = layout.tiles[i]?.kind;
    if (!isRoad(kind)) continue;
    const at = coordOf(i, layout.size);
    road.push(at);
    if (kind === "mud") mud.push(at);
    if (kind === "barricade") barricade.push(at);
  }

  // Mud and barricades sit ON the road, so the road budget is the total length.
  if (road.length > budget.roadTiles) {
    return fail(
      "ROAD_BUDGET_EXCEEDED",
      `Wall ${layout.wallLevel} allows ${budget.roadTiles} road tiles; this layout uses ${road.length}.`,
      road.slice(budget.roadTiles)
    );
  }
  if (mud.length > budget.mudTiles) {
    return fail(
      "MUD_BUDGET_EXCEEDED",
      budget.mudTiles === 0
        ? `Wall ${layout.wallLevel} does not allow mud tiles yet.`
        : `Wall ${layout.wallLevel} allows ${budget.mudTiles} mud tiles; this layout uses ${mud.length}.`,
      mud.slice(budget.mudTiles)
    );
  }
  if (barricade.length > budget.barricades) {
    return fail(
      "BARRICADE_BUDGET_EXCEEDED",
      `Wall ${layout.wallLevel} allows ${budget.barricades} barricades; this layout uses ${barricade.length}.`,
      barricade.slice(budget.barricades)
    );
  }

  return undefined;
}

function lastWallLevel(): number {
  // defenceBudget() already clamps, but computing the cap here keeps the
  // message honest about which level's budget was applied.
  let cap = 0;
  for (let level = 0; level <= 100; level++) {
    if (defenceBudget(level).wallLevel !== level) break;
    cap = level;
  }
  return cap;
}

// --- towers ---------------------------------------------------------------

function checkTowers(layout: Layout): Failure | undefined {
  const slots: Coord[] = [];
  const built: { at: Coord; kind: TowerKind; level: number }[] = [];

  for (let i = 0; i < layout.tiles.length; i++) {
    const tile = layout.tiles[i];
    if (tile?.kind !== "tower_slot") continue;
    const at = coordOf(i, layout.size);
    slots.push(at);
    if (tile.tower) built.push({ at, kind: tile.tower.kind, level: tile.tower.level });
  }

  const allowedSlots = towerSlots(layout.engineersGuildLevel);
  if (slots.length > allowedSlots) {
    return fail(
      "TOWER_SLOT_BUDGET_EXCEEDED",
      `Engineer's Guild ${layout.engineersGuildLevel} allows ${allowedSlots} tower slots; this layout uses ${slots.length}.`,
      slots.slice(allowedSlots)
    );
  }

  // A slot must be able to see the road, or it is a wasted tile that would
  // silently do nothing in the battle.
  const stranded = slots.filter((s) => !hasRoadWithinRange(layout, s));
  if (stranded.length > 0) {
    return fail(
      "TOWER_SLOT_OUT_OF_RANGE",
      "Every tower slot must be within reach of the road.",
      stranded
    );
  }

  const limit = maxTowersOfOneType(layout.engineersGuildLevel);
  for (const kind of TOWER_KINDS) {
    const ofKind = built.filter((t) => t.kind === kind);
    if (ofKind.length > limit) {
      return fail(
        "TOWER_TYPE_LIMIT",
        `No more than ${limit} towers of one type (anti-dominant-strategy rule); this layout has ${ofKind.length} ${kind}.`,
        ofKind.slice(limit).map((t) => t.at)
      );
    }
  }

  for (const tower of built) {
    const stats = TOWER_KINDS.includes(tower.kind) ? towerStats(tower.kind, 1) : undefined;
    if (!stats) {
      return fail("TOWER_UNKNOWN", `"${tower.kind}" is not a tower.`, [tower.at]);
    }
    if (!Number.isInteger(tower.level) || tower.level < 1 || tower.level > stats.levels.length) {
      return fail(
        "TOWER_LEVEL_INVALID",
        `${stats.name} has levels 1-${stats.levels.length}, not ${tower.level}.`,
        [tower.at]
      );
    }
    if (layout.engineersGuildLevel < stats.unlockEngineersGuild) {
      return fail(
        "TOWER_LOCKED",
        `${stats.name} unlocks at Engineer's Guild ${stats.unlockEngineersGuild}.`,
        [tower.at]
      );
    }
  }

  return undefined;
}

/**
 * True when any road tile lies within the longest tower range in the game.
 *
 * Uses the widest range rather than the slot's own tower so that an empty slot
 * is judged on what could be built there, not on what happens to be there now.
 */
function hasRoadWithinRange(layout: Layout, slot: Coord): boolean {
  const reachTiles = Math.ceil(widestRangeMilli() / 1000);
  for (let dy = -reachTiles; dy <= reachTiles; dy++) {
    for (let dx = -reachTiles; dx <= reachTiles; dx++) {
      const x = slot.x + dx;
      const y = slot.y + dy;
      if (!inBounds(x, y, layout.size)) continue;
      if (dx * dx + dy * dy > reachTiles * reachTiles) continue;
      if (isRoad(tileAt(layout, x, y)?.kind)) return true;
    }
  }
  return false;
}

let widestRange: number | undefined;
function widestRangeMilli(): number {
  widestRange ??= TOWER_KINDS.reduce(
    (max, kind) => Math.max(max, towerStats(kind, 1).rangeMilli),
    0
  );
  return widestRange;
}

/**
 * Rejects illegal attack plans before a battle is simulated.
 *
 * The caps come from the Assumptions sheet and exist to bound how much work one
 * attack can ask the server to do, as much as to shape the game.
 */
export function validatePlan(plan: AttackPlan): ValidationResult {
  if (plan.waves.length === 0) {
    return fail("PLAN_EMPTY", "An attack needs at least one wave.");
  }
  if (plan.waves.length > MAX_WAVES) {
    return fail(
      "TOO_MANY_WAVES",
      `An attack may be split into at most ${MAX_WAVES} waves; this plan has ${plan.waves.length}.`
    );
  }

  let total = 0;
  for (const [index, wave] of plan.waves.entries()) {
    if (wave.units.length === 0) {
      return fail("WAVE_EMPTY", `Wave ${index + 1} has no units in it.`);
    }
    if (!Number.isInteger(wave.offsetTicks) || wave.offsetTicks < 0) {
      return fail("WAVE_OFFSET_INVALID", `Wave ${index + 1} has an invalid entry time.`);
    }
    if (wave.offsetTicks > MAX_WAVE_OFFSET_TICKS) {
      return fail(
        "WAVE_OFFSET_TOO_LATE",
        `A wave may enter at most ${MAX_WAVE_OFFSET_TICKS / 10} s after the first.`
      );
    }
    for (const kind of wave.units) {
      if (!UNIT_KINDS.includes(kind)) {
        return fail("UNIT_UNKNOWN", `"${kind}" is not a unit.`);
      }
    }
    total += wave.units.length;
  }

  if (total > MAX_UNITS_PER_ATTACK) {
    return fail(
      "TOO_MANY_UNITS",
      `An attack may send at most ${MAX_UNITS_PER_ATTACK} units; this plan sends ${total}.`
    );
  }

  return { ok: true };
}

/** Exported for the editor, which highlights the road as the defender draws. */
export function roadTileCount(layout: Layout): number {
  let count = 0;
  for (const tile of layout.tiles) if (isRoad(tile.kind)) count++;
  return count;
}

export { idx };
